import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, CircularProgress, Box,
} from '@mui/material';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { notesService } from '../../services/notes.service';
import { useSaveNote, useDeleteNote } from '../../hooks/useNotes';
import type { NoteType } from '../../types/api.types';

interface NoteModalProps {
  open: boolean;
  onClose: () => void;
  segmentId: number;
  noteType: NoteType;
  sourceText: string;
  hasExistingNote: boolean;
}

export const NoteModal = ({ open, onClose, segmentId, noteType, sourceText, hasExistingNote }: NoteModalProps) => {
  const { t } = useTranslation('segments');
  const [noteText, setNoteText] = useState('');
  const [isLoadingNote, setIsLoadingNote] = useState(true);
  const [noteExists, setNoteExists] = useState(hasExistingNote);
  const saveNote = useSaveNote();
  const deleteNote = useDeleteNote();

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setIsLoadingNote(true);
    notesService.getNote(segmentId, noteType).then((note) => {
      if (cancelled) return;
      if (note) {
        setNoteText(note.noteText);
        setNoteExists(true);
      } else {
        setNoteText(sourceText);
        setNoteExists(false);
      }
      setIsLoadingNote(false);
    }).catch((error: Error) => {
      if (cancelled) return;
      toast.error(t('noteFailedToLoad', { message: error.message }));
      // We don't know whether a note actually exists on the server, so
      // default noteExists to false (hides Delete) to avoid offering a
      // destructive action against an unknown state. Fall back to
      // sourceText so the form isn't left blank.
      setNoteText(sourceText);
      setNoteExists(false);
      setIsLoadingNote(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, segmentId, noteType]);

  const handleClose = () => {
    setNoteText('');
    setIsLoadingNote(true);
    onClose();
  };

  const handleSave = () => {
    saveNote.mutate(
      { segmentId, noteType, noteText },
      { onSuccess: handleClose }
    );
  };

  const handleDelete = () => {
    if (!window.confirm(t('noteDeleteConfirm'))) return;
    deleteNote.mutate(
      { segmentId, noteType },
      { onSuccess: handleClose }
    );
  };

  const title = noteType === 'transcript' ? t('noteTitleTranscript') : t('noteTitleTranslation');

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        {isLoadingNote ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <TextField
            label={t('noteFieldLabel')}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            multiline
            minRows={6}
            fullWidth
            autoFocus
          />
        )}
      </DialogContent>
      <DialogActions>
        {noteExists && (
          <Button color="error" onClick={handleDelete} disabled={deleteNote.isPending}>
            {t('noteDelete')}
          </Button>
        )}
        <Button onClick={handleClose}>{t('cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isLoadingNote || saveNote.isPending}
        >
          {t('noteSave')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
